import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Input } from '@/components/form/Input';
import { FormBlock } from '@/components/FormBlock';
import { useReviewMode } from '@/hooks/useReviewMode';
import { useZustandSync } from '@/hooks/useZustandSync';
import { authStore } from '@/stores/auth';
import useProductStore, { productStore } from '@/stores/productStore';

import { Toggle } from '../form/Toggle';
import { HelpText } from '../ui/HelpText';
import ReviewPaymentMethods from './ReviewMode/ReviewPaymentMethods';

const productPaymentMethodSchema = z
  .object({
    creditCardMethod: z.boolean(),
    cardHolderName: z.string().optional(),
    pixMethod: z.boolean(),
    bankslip: z.boolean(),
    geral: z.string().optional()
  })
  .superRefine((data, ctx) => {
    if (!data.creditCardMethod && !data.pixMethod && !data.bankslip) {
      ctx.addIssue({
        code: 'custom',
        path: ['geral'],
        message: 'Selecione pelo menos um método de pagamento.'
      });
    }

    if (data.creditCardMethod && !data.cardHolderName) {
      ctx.addIssue({
        code: 'custom',
        path: ['cardHolderName'],
        message: 'Digite o nome que aparecerá na fatura do cartão.'
      });
    }
  });

type ProductPaymentMethodFormValues = z.infer<typeof productPaymentMethodSchema>;

export default function ProductPaymentMethods() {
  const currentPaymentMethod = useProductStore(d => d.payment);
  const reviewMode = useReviewMode();

  const form = useForm<ProductPaymentMethodFormValues>({
    resolver: zodResolver(productPaymentMethodSchema),
    mode: 'onChange',
    defaultValues: {
      creditCardMethod: currentPaymentMethod.methods?.length
        ? currentPaymentMethod.methods?.some(method => method.type === 'credit_card')
        : true,
      cardHolderName:
        productStore.getState().payment?.methods?.find(method => method.type === 'credit_card')?.cardInfo ||
        authStore.getState().currentUser?.name?.slice(0, 10) ||
        '',
      pixMethod: currentPaymentMethod.methods?.length
        ? currentPaymentMethod.methods?.some(method => method.type === 'pix')
        : true,
      bankslip: currentPaymentMethod.methods?.length
        ? currentPaymentMethod.methods?.some(method => method.type === 'bankslip')
        : true
    }
  });

  useZustandSync<ProductPaymentMethodFormValues>(form.control, (data, state) => ({
    payment: {
      ...state.payment,
      methods: [
        ...(data.creditCardMethod ? [{ type: 'credit_card', cardInfo: data.cardHolderName }] : []),
        ...(data.pixMethod ? [{ type: 'pix' }] : []),
        ...(data.bankslip ? [{ type: 'bankslip' }] : [])
      ] as ({ type: 'credit_card'; cardInfo: string } | { type: 'bankslip' | 'pix' })[]
    }
  }));

  const countSelected = Object.values(currentPaymentMethod.methods || []).filter(value => value).length;
  return (
    <FormBlock
      name='product-payment-methods'
      subtitle={reviewMode ? undefined : `${countSelected} selecionados`}
      form={form}
      title='Meios de pagamento'
      preview={<ReviewPaymentMethods />}
    >
      <div className='flex flex-col divide-y divide-black/6'>
        <div className='flex flex-col gap-4 py-4'>
          {form.formState?.errors?.geral && (
            <p className='text-sm text-red-500'>{form.formState.errors.geral?.message ?? ''}</p>
          )}
          <div id='geral' className='flex items-center justify-between'>
            <div>
              <p className='mb-0! text-sm font-semibold'>Cartão de crédito</p>
              <HelpText size='sm'>Permite que seus clientes paguem com cartão de crédito.</HelpText>
            </div>
            <div>
              <Toggle id='toggle-credit-card' name='creditCardMethod' />
            </div>
          </div>

          <div hidden={!form.getValues('creditCardMethod')}>
            <span className='text-content-caption text-sm'>Nome na fatura do cartão</span>
            <Input
              id='input-credit-card-name'
              placeholder='Nome na fatura do cartão'
              name='cardHolderName'
              maxLength={10}
              prefix={
                <span className='mr-1 border-r border-gray-200 pr-2 text-gray-600 dark:text-neutral-300'>EDZ</span>
              }
              showCount
            />
          </div>

          <div
            className='text-content-disabled dark:text-content-caption flex flex-col gap-3 text-sm'
            hidden={!form.getValues('creditCardMethod')}
          >
            <span>O nome que você definir será exibido na fatura do seu cliente.</span>
            <span>Escolha algo que ele reconheça facilmente para evitar transtornos.</span>
          </div>
        </div>

        <div className='flex flex-col gap-4 py-4'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='mb-0! text-sm font-semibold'>Pix</p>
              <HelpText size='sm'>Permite que seus clientes paguem com pix.</HelpText>
            </div>
            <div>
              <Toggle id='toggle-pix' name='pixMethod' />
            </div>
          </div>
        </div>

        <div className='flex flex-col gap-4 py-4'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='mb-0! text-sm font-semibold'>Boleto</p>
              <HelpText size='sm'>Permite que seus clientes paguem com boleto.</HelpText>
            </div>
            <div>
              <Toggle id='toggle-invoice' name='bankslip' />
            </div>
          </div>
        </div>
      </div>
    </FormBlock>
  );
}
